import React from 'react'
import tw, { styled } from 'twin.macro'
import SEO from '../components/SEO/SEO'

const PageWrapper = styled.div`
  background: #fff;
  min-height: 100vh;
  padding: 138px 0 80px 0;
`

const ContentWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
`

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 2rem;
`

const LastUpdated = styled.p`
  color: #666;
  margin-bottom: 2rem;
  font-size: 0.9rem;
`

const Section = styled.section`
  margin-bottom: 2rem;
`

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 1rem;
`

const Paragraph = styled.p`
  color: #333;
  line-height: 1.8;
  margin-bottom: 1rem;
`

const List = styled.ul`
  color: #333;
  line-height: 1.8;
  margin-bottom: 1rem;
  padding-left: 1.5rem;
  list-style-type: disc;
`

const ListItem = styled.li`
  margin-bottom: 0.5rem;
`

const PrivacyPage = ({ pageContext: { locale } }) => {
  return (
    <main>
      <SEO
        title="Privacy Policy"
        desc="Learn how Bridment handles and protects your data."
        locale={locale}
      />
      <PageWrapper>
        <ContentWrapper>
          <Title>Privacy Policy</Title>
          <LastUpdated>Last updated: February 2026</LastUpdated>

          <Section>
            <SectionTitle>Introduction</SectionTitle>
            <Paragraph>
              Bridment ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Shopify applications and services.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>Information We Collect</SectionTitle>
            <Paragraph>
              When you install and use our Shopify apps, we may collect the following types of information:
            </Paragraph>
            <List>
              <ListItem>
                <strong>Store Information:</strong> Your Shopify store name, domain, email address, and other account details necessary for the app to function.
              </ListItem>
              <ListItem>
                <strong>Product and Order Data:</strong> Information about your products, orders, and customers as required by the app's functionality.
              </ListItem>
              <ListItem>
                <strong>Usage Data:</strong> Information about how you interact with our apps, including features used and actions taken.
              </ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>How We Use Your Information</SectionTitle>
            <Paragraph>
              We use the information we collect to:
            </Paragraph>
            <List>
              <ListItem>Provide, maintain, and improve our apps and services</ListItem>
              <ListItem>Process transactions and send related information</ListItem>
              <ListItem>Respond to your comments, questions, and provide customer support</ListItem>
              <ListItem>Send you technical notices, updates, and administrative messages</ListItem>
              <ListItem>Monitor and analyze trends, usage, and activities</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>Data Sharing and Disclosure</SectionTitle>
            <Paragraph>
              We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
            </Paragraph>
            <List>
              <ListItem>With your consent or at your direction</ListItem>
              <ListItem>With service providers who assist us in operating our apps</ListItem>
              <ListItem>To comply with legal obligations or protect our rights</ListItem>
              <ListItem>In connection with a merger, acquisition, or sale of assets</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>Data Security</SectionTitle>
            <Paragraph>
              We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>Data Retention</SectionTitle>
            <Paragraph>
              We retain your information for as long as your account is active or as needed to provide you services. When you uninstall our app, we will delete your data within 30 days, unless we are required to retain it for legal purposes.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>Your Rights</SectionTitle>
            <Paragraph>
              You have the right to:
            </Paragraph>
            <List>
              <ListItem>Access and receive a copy of your personal data</ListItem>
              <ListItem>Request correction of inaccurate data</ListItem>
              <ListItem>Request deletion of your data</ListItem>
              <ListItem>Object to or restrict processing of your data</ListItem>
              <ListItem>Data portability</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>GDPR Compliance</SectionTitle>
            <Paragraph>
              For users in the European Economic Area (EEA), we comply with the General Data Protection Regulation (GDPR). We process your data based on legitimate interests, contract performance, or your consent.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>CCPA Compliance</SectionTitle>
            <Paragraph>
              For California residents, we comply with the California Consumer Privacy Act (CCPA). You have the right to know what personal information we collect, request deletion of your data, and opt-out of the sale of your personal information (we do not sell personal information).
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>Changes to This Policy</SectionTitle>
            <Paragraph>
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>Contact Us</SectionTitle>
            <Paragraph>
              If you have any questions about this Privacy Policy or our data practices, please contact us at:
            </Paragraph>
            <Paragraph>
              <strong>Email:</strong> privacy@bridment.com
            </Paragraph>
          </Section>
        </ContentWrapper>
      </PageWrapper>
    </main>
  )
}

export default PrivacyPage
